import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

const API_URL = '/api';

const initialFields = {
  SubcategoryCode: '', SubcategoryName: '', ParentCategoryCode: '', Description: '', Status: 'Active'
};

const SubcategoryFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  // Fetch current user
  useEffect(() => {
    fetch(`${API_URL}/current-user`)
      .then(res => res.json())
      .then(data => {
        if (data.user) {
          setCurrentUser(data.user);
        }
      })
      .catch(error => {
        console.error('Error fetching current user:', error);
      });
  }, []);

  // Fetch categories for dropdown
  useEffect(() => {
    setIsLoadingCategories(true);
    fetch(`${API_URL}/categories`)
      .then(res => res.json())
      .then(data => {
        setCategories(data);
      })
      .catch(error => {
        console.error('Error fetching categories:', error);
      })
      .finally(() => {
        setIsLoadingCategories(false);
      });
  }, []);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFields({ ...initialFields, ...initialData });
      } else {
        // Generate next subcategory code when creating new subcategory
        setIsLoadingCode(true);
        generateNextSubcategoryCode()
          .then(nextCode => {
            setFields(prev => ({ ...prev, SubcategoryCode: nextCode }));
          })
          .catch(error => {
            console.error('Error generating subcategory code:', error);
            setFields(prev => ({ ...prev, SubcategoryCode: 'SUB-001' }));
          })
          .finally(() => {
            setIsLoadingCode(false);
          });
      }
      setErrors({});
    }
  }, [open, initialData, isEdit]);

  const generateNextSubcategoryCode = async () => {
    try {
      const response = await fetch(`${API_URL}/subcategorylatestcode`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.nextCode;
    } catch (error) {
      console.error('Error fetching next subcategory code:', error);
      return 'SUB-001';
    }
  };

  const handleChange = e => {
    setFields({ ...fields, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const errs = {};
    if (!fields.SubcategoryName) errs.SubcategoryName = 'Required';
    if (!fields.ParentCategoryCode) errs.ParentCategoryCode = 'Required';
    if (!fields.Status) errs.Status = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    const formData = {
      ...fields,
      CreatedBy: currentUser?.UserID || 1,
      UpdatedBy: currentUser?.UserID || 1
    };
    
    try {
      onSubmit(formData);
    } catch (error) {
      console.error('Error saving subcategory:', error);
      alert(error.message || 'An error occurred while saving the subcategory');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-2xl p-4 sm:p-8 relative overflow-y-auto max-h-[90vh]">
        <button className="absolute top-4 right-4 text-[#2b6777] hover:text-[#52ab98]" onClick={onClose}><FaTimes size={20} /></button>
        <h2 className="text-2xl font-bold text-[#2b6777] mb-6 text-center">{isEdit ? 'Edit Subcategory' : 'Create New Subcategory'}</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Subcategory Information */}
          <div className='bg-white p-6 rounded-xl shadow-md'>
            <h3 className="text-lg font-bold text-[#2b6777] mb-4">Subcategory Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">Subcategory Code</label>
                <input 
                  name="SubcategoryCode" 
                  value={isLoadingCode ? 'Loading...' : fields.SubcategoryCode} 
                  readOnly 
                  className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700" 
                />
              </div>
              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">Subcategory Name *</label>
                <input 
                  name="SubcategoryName" 
                  autoComplete="off"
                  value={fields.SubcategoryName} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.SubcategoryName ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                />
                {errors.SubcategoryName && <span className="text-xs text-red-500">{errors.SubcategoryName}</span>}
              </div>
              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">Parent Category *</label>
                <select 
                  name="ParentCategoryCode" 
                  value={fields.ParentCategoryCode} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.ParentCategoryCode ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                  disabled={isLoadingCategories}
                >
                  <option value="">Select Parent Category</option>
                  {categories.map(category => (
                    <option key={category.CategoryCode} value={category.CategoryCode}>
                      {category.CategoryName}
                    </option>
                  ))}
                </select>
                {errors.ParentCategoryCode && <span className="text-xs text-red-500">{errors.ParentCategoryCode}</span>}
              </div>
              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">Status *</label>
                <select 
                  name="Status" 
                  value={fields.Status} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.Status ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {errors.Status && <span className="text-xs text-red-500">{errors.Status}</span>}
              </div>
              <div className='flex flex-col gap-2 md:col-span-2'>
                <label className="block text-sm font-medium text-[#2b6777]">Description</label>
                <textarea 
                  name="Description" 
                  autoComplete="off"
                  value={fields.Description} 
                  onChange={handleChange} 
                  rows="3"
                  className="w-full border border-[#c8d8e4] rounded px-3 py-2" 
                />
              </div>
              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#2b6777]">Created Date</label>
                <input 
                  value={new Date().toLocaleString()} 
                  readOnly 
                  className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700" 
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6">
            <button type="button" className="px-4 py-2 rounded bg-gray-200 text-[#2b6777] font-semibold" onClick={onClose}>Cancel</button>
            <button type="submit" className="px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow" disabled={loading}>{loading ? 'Saving...' : (isEdit ? 'Update Subcategory' : 'Create Subcategory')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubcategoryFormModal; 